import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Trophy } from "lucide-react";

import { AnnouncementBanner, LocationSchedule, CREAM, FollowButton, GREEN, INK } from "../components/tournament/PublicParts";
import { STAGES, decidedText, groupRounds, stageLabel, stageOf } from "../components/tournament/BracketTab";
import { StandingsTable } from "../components/tournament/PoolsPanel";
import { roundLabel } from "../components/tournament/shared";
import { sportLabel } from "../lib/sports";
import { publicManifestUrl } from "../lib/tournamentApi";
import { usePublicTournament } from "../lib/usePublicTournament";
import { BrandLogo, SponsorStrip } from "../components/tournament/Brand";
import NextTournamentSignup from "../components/tournament/NextTournamentSignup";

/** Public Tournament Hub — no login. Polls every 15s (browser revalidates via ETag). */
export default function PublicTournament() {
  const { slug } = useParams();
  const { data, error } = usePublicTournament(slug);
  const [divisionId, setDivisionId] = useState(null);
  const [view, setView] = useState("bracket");

  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return undefined;
    const prev = link.getAttribute("href");
    link.setAttribute("href", publicManifestUrl(slug));
    return () => link.setAttribute("href", prev);
  }, [slug]);

  if (error && !data) return <div style={{ padding: 24, color: "#B22222" }} data-testid="public-error">{error}</div>;
  if (!data) return <div style={{ padding: 24, color: CREAM, background: INK, minHeight: "100vh" }}>Loading…</div>;

  const t = data.tournament;
  const hub = t.tier === "hub";
  const divisions = data.divisions;
  const division = divisions.find((d) => d.id === divisionId) || divisions[0];
  const teamsById = Object.fromEntries(data.teams.map((x) => [x.id, x]));
  const views = hub ? [["bracket", "Bracket"], ["locations", "By location"], ["teams", "Teams"]] : [];

  return (
    <div style={{ background: INK, minHeight: "100vh", color: CREAM, padding: "20px 18px 60px", fontFamily: "system-ui, sans-serif" }}
         data-testid="public-tournament">
      <style>{"@keyframes pt-fade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }"}</style>
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        <AnnouncementBanner items={data.announcements || []} />
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", marginBottom: 18 }}>
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            <BrandLogo branding={data.branding} />
            <div>
            <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 40, margin: 0, letterSpacing: "0.02em", lineHeight: 1 }}
                data-testid="public-title">{t.name}</h1>
            <div style={{ fontSize: 13, opacity: 0.75, marginTop: 4 }}>
              {sportLabel(t.sport, t.sport_custom)} · {t.team_type}{t.location ? ` · ${t.location}` : ""}
              {t.status === "complete" && <span style={{ color: "#4db87a", marginLeft: 8, fontWeight: 700 }}>· COMPLETE</span>}
            </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {(data.results || []).some((r) => r.decided) && (
              <Link to={`/t/${slug}/results`} data-testid="public-results-link"
                    style={{ display: "inline-flex", gap: 6, alignItems: "center", border: "0.5px solid rgba(245,240,232,0.35)", color: CREAM,
                             borderRadius: 999, padding: "9px 16px", fontWeight: 800, fontSize: 13, textDecoration: "none" }}>
                <Trophy size={15} aria-hidden="true" /> Results</Link>
            )}
            {t.follow_enabled && <FollowButton slug={slug} teams={data.teams} divisions={divisions} />}
          </div>
        </div>
        {(divisions.length > 1 || views.length > 0) && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
            {views.map(([k, label]) => (
              <button key={k} data-testid={`public-view-${k}`} onClick={() => setView(k)} style={tab(view === k, CREAM, INK)}>{label}</button>
            ))}
            {divisions.length > 1 && <span style={{ width: 1, background: "rgba(245,240,232,0.2)", margin: "0 4px" }} />}
            {divisions.length > 1 && divisions.map((d) => (
              <button key={d.id} data-testid={`public-division-${d.id}`} onClick={() => setDivisionId(d.id)}
                      style={tab(division.id === d.id, GREEN, CREAM)}>{d.name}</button>
            ))}
          </div>
        )}
        {view === "bracket" && <Stages division={division} games={data.games.filter((g) => g.division_id === division.id)}
                                       teamsById={teamsById} pools={data.pools || []} />}
        {view === "locations" && <LocationSchedule data={data} divisionId={division.id} teamsById={teamsById} />}
        {view === "teams" && <Teams data={data} division={division} />}
        <NextTournamentSignup slug={slug} enabled={data.signup_enabled} />
        <div style={{ marginTop: 36 }}><SponsorStrip branding={data.branding} tile={48} /></div>
      </div>
    </div>
  );
}

const tab = (active, bg, fg) => ({
  padding: "7px 14px", borderRadius: 999, border: "0.5px solid rgba(245,240,232,0.25)", cursor: "pointer",
  background: active ? bg : "transparent", color: active ? fg : CREAM, fontWeight: 700, fontSize: 12,
  transition: "background-color 150ms ease, color 150ms ease",
});

export function Stages({ division, games, teamsById, pools }) {
  const present = STAGES.map(([k]) => k).filter((k) => games.some((g) => stageOf(g) === k));
  const [stage, setStage] = useState(null);
  const active = present.includes(stage) ? stage : present.find((k) => k !== "pool") || present[0];
  const poolName = Object.fromEntries(pools.map((p) => [p.id, p.name]));
  const consPending = division.consolation === "first_game" && games.length > 0 && !games.some((g) => g.stage === "consolation");
  return (
    <div data-testid="public-stages">
      {present.length > 1 && (
        <div style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 12 }}>
          {present.map((k) => (
            <button key={k} data-testid={`public-stage-${k}`} onClick={() => setStage(k)} style={tab(active === k, GREEN, CREAM)}>
              {stageLabel(k, division)}</button>
          ))}
        </div>
      )}
      {consPending && <div data-testid="public-consolation-pending" style={{ fontSize: 12, opacity: 0.7, marginBottom: 10 }}>
        Consolation bracket set after Round 2.</div>}
      {active === "pool" ? (
        (division.standings || []).map((p) => (
          <div key={p.pool_id} style={{ marginBottom: 18 }} data-testid={`public-standings-${p.pool_id}`}>
            <div style={{ textTransform: "uppercase", letterSpacing: "0.1em", fontSize: 11, opacity: 0.6, marginBottom: 6 }}>
              {poolName[p.pool_id] || p.name}{p.rows[0]?.provisional ? " · provisional" : ""}</div>
            <div style={{ background: CREAM, color: INK, borderRadius: 8, padding: 6 }}>
              <StandingsTable rows={p.rows} name={(id) => teamsById[id]?.name || "TBD"} testId={`public-table-${p.pool_id}`} />
            </div>
          </div>
        ))
      ) : (
        <Bracket games={games.filter((g) => stageOf(g) === active)} teamsById={teamsById} stage={active} />
      )}
    </div>
  );
}

function Bracket({ games, teamsById, stage }) {
  const { rounds, roundKeys } = groupRounds(games);
  if (!games.length) return <div style={{ opacity: 0.7 }} data-testid="public-bracket-empty">Bracket coming soon.</div>;
  const max = roundKeys[roundKeys.length - 1];
  return roundKeys.map((r) => (
    <div key={r} style={{ marginBottom: 20 }}>
      <div style={{ textTransform: "uppercase", letterSpacing: "0.1em", fontSize: 11, opacity: 0.6, marginBottom: 6 }}>
        {stage === "final" ? (rounds[r][0].stage === "if_necessary" ? "If necessary" : "Grand final") : stage === "losers" ? `Losers round ${roundKeys.indexOf(r) + 1}` : stage === "third_place" ? "3rd place" : roundLabel(r, max)}</div>
      {rounds[r].map((g) => {
        const name = (id) => (id ? teamsById[id]?.name : g.is_bye ? "Bye" : "TBD");
        return (
          <div key={g.id} data-testid={`public-game-${g.id}`}
               style={{ background: CREAM, color: INK, borderRadius: 6, padding: 10, marginBottom: 6,
                        border: g.winner_team_id ? `0.5px solid ${GREEN}` : "0.5px solid transparent" }}>
            <Row name={name(g.home_team_id)} score={g.home_score} winner={g.winner_team_id && g.winner_team_id === g.home_team_id} />
            <Row name={name(g.away_team_id)} score={g.away_score} winner={g.winner_team_id && g.winner_team_id === g.away_team_id} />
            <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {g.court || "—"} · {g.is_bye ? "bye" : g.status === "not_needed" ? "not needed" : g.status}
              {g.decided_by && <span data-testid={`public-decided-${g.id}`}> · {decidedText(g)}</span>}
              {g.set_scores?.length > 0 && <span> · {g.set_scores.map((x) => x.join("-")).join(", ")}</span>}
            </div>
          </div>
        );
      })}
    </div>
  ));
}

function Row({ name, score, winner }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontWeight: winner ? 700 : 400, color: winner ? GREEN : "inherit" }}>
      <span>{name}</span><span>{score ?? "—"}</span>
    </div>
  );
}

function Teams({ data, division }) {
  const teams = data.teams.filter((x) => x.division_id === division.id);
  return (
    <div data-testid="public-teams" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
      {teams.map((tm) => {
        const roster = data.players.filter((p) => p.team_id === tm.id);
        return (
          <div key={tm.id} data-testid={`public-team-${tm.id}`} style={{ background: "rgba(245,240,232,0.06)", borderRadius: 10, padding: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700 }}>
              <span>{tm.name}</span>
              {tm.checked_in && <span style={{ fontSize: 11, color: "#4db87a" }}>Checked in</span>}
            </div>
            {data.tournament.show_rosters && roster.map((p) => (
              <div key={p.id} style={{ fontSize: 13, opacity: 0.85, marginTop: 4 }}>
                {p.jersey ? `#${p.jersey} ` : ""}{p.first_name} {p.last_initial}.
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
